/**
 * Supplier picker for the "Custom (Selected Suppliers)" price book sharing
 * state — CR-6346 rework.
 *
 * QA rejected the first pass because only All Off and All On existed. Custom
 * needs somewhere to say *which* suppliers, which is this. Each grant is stored
 * per relationship on the server (SupplierCustomerRelationship.price_book_shared),
 * so a grant survives the global flag being switched away and back.
 *
 * Saves optimistically one supplier at a time and rolls that row back on
 * failure, matching how the rest of this settings page behaves.
 */
import { useState, useEffect, useMemo } from 'react';
import { Label } from '../../ui/label';
import { Checkbox } from '../../ui/checkbox';
import { Input } from '../../ui/input';
import { Button } from '../../ui/button';
import { Loader2, Search, AlertCircle } from 'lucide-react';
import { platformSettingsAPI } from '../../../lib/api';
import type { PriceBookSharingSupplier } from '../../../lib/api';
import { cn } from '../../../lib/utils';
import { toast } from 'sonner';

interface PriceBookSupplierPickerProps {
  /** Bumped by the parent whenever a save should be reflected here. */
  refreshKey?: number;
}

export function PriceBookSupplierPicker({
  refreshKey = 0,
}: PriceBookSupplierPickerProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [suppliers, setSuppliers] = useState<PriceBookSharingSupplier[]>([]);
  const [search, setSearch] = useState('');
  const [savingIds, setSavingIds] = useState<number[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await platformSettingsAPI.getPriceBookSharingSuppliers();
        if (cancelled) return;
        setSuppliers(res.data.results ?? []);
      } catch (err: unknown) {
        if (cancelled) return;
        console.error('Error loading price book sharing suppliers:', err);
        setError('Failed to load suppliers');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  const grantedCount = useMemo(
    () => suppliers.filter((s) => s.price_book_shared).length,
    [suppliers],
  );

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return suppliers;
    return suppliers.filter(
      (s) =>
        s.supplier_name.toLowerCase().includes(term) ||
        s.supplier_code.toLowerCase().includes(term),
    );
  }, [suppliers, search]);

  const applyLocal = (ids: number[], shared: boolean) => {
    setSuppliers((prev) =>
      prev.map((s) =>
        ids.includes(s.supplier_id) ? { ...s, price_book_shared: shared } : s,
      ),
    );
  };

  const save = async (ids: number[], shared: boolean) => {
    if (ids.length === 0) return;
    const previous = suppliers;
    applyLocal(ids, shared);
    setSavingIds((prev) => [...prev, ...ids]);
    try {
      await platformSettingsAPI.updatePriceBookSharingSuppliers(
        ids.map((supplier_id) => ({ supplier_id, price_book_shared: shared })),
      );
      toast.success(
        ids.length === 1
          ? `Price book access ${shared ? 'granted' : 'revoked'}`
          : `Price book access ${shared ? 'granted' : 'revoked'} for ${ids.length} suppliers`,
      );
    } catch (err: unknown) {
      console.error('Error saving supplier price book grants:', err);
      toast.error('Failed to update supplier access');
      setSuppliers(previous);
    } finally {
      setSavingIds((prev) => prev.filter((id) => !ids.includes(id)));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center rounded-md border border-input py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{error}</span>
      </div>
    );
  }

  if (suppliers.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-input px-4 py-3 text-sm text-muted-foreground">
        No active suppliers for this company. Add supplier relationships before
        choosing who can view price books.
      </p>
    );
  }

  const visibleIds = visible.map((s) => s.supplier_id);
  const allVisibleGranted =
    visible.length > 0 && visible.every((s) => s.price_book_shared);

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Label className="text-sm font-medium">
          Selected suppliers
          <span className="ml-2 font-normal text-muted-foreground">
            {grantedCount} of {suppliers.length} selected
          </span>
        </Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => save(visibleIds, !allVisibleGranted)}
          disabled={visible.length === 0 || savingIds.length > 0}
        >
          {allVisibleGranted ? 'Clear all' : 'Select all'}
        </Button>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search suppliers"
          className="pl-9"
        />
      </div>

      {visible.length === 0 ? (
        <p className="rounded-md border border-dashed border-input px-4 py-3 text-sm text-muted-foreground">
          No suppliers match &ldquo;{search}&rdquo;.
        </p>
      ) : (
        <div className="max-h-72 space-y-2 overflow-y-auto pr-1">
          {visible.map((s) => {
            const saving = savingIds.includes(s.supplier_id);
            return (
              <label
                key={s.supplier_id}
                htmlFor={`pb-supplier-${s.supplier_id}`}
                className={cn(
                  'flex items-center gap-3 rounded-md border border-input bg-background px-4 py-3',
                  saving ? 'cursor-wait opacity-60' : 'cursor-pointer hover:bg-muted',
                )}
              >
                <Checkbox
                  id={`pb-supplier-${s.supplier_id}`}
                  checked={s.price_book_shared}
                  disabled={saving}
                  onCheckedChange={(checked) =>
                    save([s.supplier_id], checked === true)
                  }
                />
                <span className="text-sm">{s.supplier_name}</span>
                {s.supplier_code && (
                  <span className="rounded bg-muted px-2 py-0.5 font-mono text-xs text-muted-foreground">
                    {s.supplier_code}
                  </span>
                )}
                {saving && (
                  <Loader2 className="ml-auto h-4 w-4 animate-spin text-muted-foreground" />
                )}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}
